import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { taskService } from '../../../services/taskService';
import './TasksBoard.css';

const TasksBoard = ({ channel }) => {
  const [columns, setColumns] = useState({});

  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [selectedArea, setSelectedArea] = useState(
    Object.keys(columns).length > 0 ? Object.keys(columns)[0] : ''
  );
  useEffect(() => {
    // Инициализация колонок из Areas канала
    const initialColumns = {};
    (channel.areas || []).forEach(area => {
      initialColumns[area] = [];
    });
    setColumns(initialColumns);

    // Загрузка задач
    taskService.getTasks(channel.id)
      .then(tasks => {
        const grouped = { ...initialColumns };
        tasks.forEach(task => {
          if (grouped[task.area]) {
            grouped[task.area].push(task);
          }
        });
        setColumns(grouped);
        setSelectedArea(Object.keys(grouped).length > 0 ? Object.keys(grouped)[0] : '')
      })
      .catch(err => console.error('Ошибка загрузки задач:', err));
  }, [channel.id, channel.areas]);

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
  
    const sourceCol = source.droppableId;
    const destCol = destination.droppableId;
    const sourceIndex = source.index;
    const destIndex = destination.index;
  
    // Обновляем локальное состояние
    const newColumns = { ...columns };
    const sourceTasks = [...newColumns[sourceCol]];
    const destTasks = sourceCol === destCol ? sourceTasks : [...newColumns[destCol]];
  
    const [removed] = sourceTasks.splice(sourceIndex, 1);
    destTasks.splice(destIndex, 0, removed);
  
    newColumns[sourceCol] = sourceTasks;
    newColumns[destCol] = destTasks;
    setColumns(newColumns);
  
    // Отправляем изменения на бэкенд
    if (sourceCol === destCol) {
      // Перемещение внутри одной колонки → сохраняем новый порядок
      const newOrder = newColumns[sourceCol].map(t => t.id);
      taskService.reorder(channel.id, sourceCol, newOrder)
        .catch(err => console.error('Ошибка сохранения порядка:', err));
    } else {
      // Перемещение между колонками → moveTask
      taskService.moveTask(draggableId, destCol, destIndex)
        .catch(err => console.error('Ошибка перемещения задачи:', err));
    }
  };

  const handleCreateTask = () => {
    if (!newTaskTitle.trim()) {
      alert('Пожалуйста, введите название задачи');
      return;
    }

    const newTask = {
      title: newTaskTitle,
      description: newTaskDescription,
      area: selectedArea,
      tags: [],
      position: columns[selectedArea].length
    };

    taskService.createTask(channel.id, newTask)
      .then(createdTask => {
        const newColumns = { ...columns };
        newColumns[selectedArea] = [...newColumns[selectedArea], createdTask];
        setColumns(newColumns);
        setShowAddTaskModal(false);
        setNewTaskTitle('');
        setNewTaskDescription('');
      })
      .catch(err => console.error('Ошибка создания задачи:', err));
  };

  const handleDeleteTask = (taskId, area) => {
    taskService.deleteTask(taskId)
      .then(() => {
        const newColumns = { ...columns };
        newColumns[area] = newColumns[area].filter(t => t.id !== taskId);
        setColumns(newColumns);
      })
      .catch(err => console.error('Ошибка удаления задачи:', err));
  };

  return (
    <div className="tasks-board">
      <div className="tasks-board-header">
        <h2>📋 Доска задач</h2>
        <button className="task-add-global-btn" onClick={() => setShowAddTaskModal(true)}>
          + Добавить задачу
        </button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="tasks-board-columns">
          {Object.entries(columns).map(([title, tasks]) => (
            <Droppable key={title} droppableId={title}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="tasks-column">
                  <div className="tasks-column-header">
                    <div className="tasks-column-title">
                      <h3>{title}</h3>
                      <span className="tasks-column-count">{tasks.length}</span>
                    </div>
                  </div>
                  <div className="tasks-column-body">
                    {tasks.map((task, index) => (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="task-card"
                          >
                            <div className="task-card-header">
                              <h4>{task.title}</h4>
                              <button
                                className="task-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task.id, title);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            <p className="task-card-description">{task.description}</p>
                            {task.tags?.length > 0 && (
                              <div className="task-card-tags">
                                {task.tags.map(tag => (
                                  <span key={tag} className="task-tag">{tag}</span>
                                ))}
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {showAddTaskModal && (
        <div className="add-task-modal-overlay" onClick={() => setShowAddTaskModal(false)}>
          <div className="add-task-modal" onClick={e => e.stopPropagation()}>
            <h3>Добавить задачу</h3>
            <div className="add-task-form">
              <div className="form-group">
                <label>Колонка</label>
                <select value={selectedArea} onChange={e => setSelectedArea(e.target.value)}>
                  {Object.keys(columns).map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Название</label>
                <input 
                  type="text" 
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Введите название задачи..." 
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea 
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  placeholder="Введите описание..." 
                />
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowAddTaskModal(false)}>Отмена</button>
                <button className="create-btn" onClick={handleCreateTask}>Создать</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksBoard;